export const selectItems = <T = Autoship>(state: StateData<T>) => {
  return state.items;
};

export const selectFormsWithUnsavedChanges = (state: StateData) => state.formsWithUnsavedChanges;

export const selectIsLoading = (state: StateData) => {
  return state.loading;
};

export const selectUserId = (state: StateData) => {
  return state.userId;
};

export const selectBillingInfo = (state: StateData) => {
  const newCardNumber =
    state.billingInfo?.cardInfo?.cardNumber?.replaceAll(' ', '') ?? state.billingInfo?.cardInfo?.cardNumber;
  return {
    ...state.billingInfo,
    cardInfo: { ...state.billingInfo.cardInfo, cardNumber: newCardNumber },
  };
};

export const selectCardInfo = (state: StateData) => {
  return state.billingInfo?.cardInfo;
};

export const selectAutoshipId = (state: StateData) => state.id;
export const selectAutoshipDetails = (state: StateData) => state.autoshipDetails;

export const selectBillingAddress = (state: StateData) => state.billingInfo?.billingAddress;

export const selectShippingAddress = (state: StateData) => state.shippingAddress;

export const selectShippingAddressLine2 = (state: StateData<Cart>) => state.shippingAddress.addressLine2;

export const selectShippingAddressLine1 = (state: StateData) => state.shippingAddress?.addressLine1;

export const selectBillingAddressLine1 = (state: StateData) => state.billingInfo?.billingAddress?.addressLine1;

export const selectCountry = (state: StateData) => state.shippingAddress?.country;

export const selectCardType = (state: StateData) => state.billingInfo?.cardInfo?.cardType;

export const selectState = <T>(state: StateData<T>) => state;

export const selectCityDropdown = (state: StateData) => state.cityDropdown;

export const selectBillingAddressSameAsShipping = (state: StateData) => {
  return state.billingInfo?.billingAddress?.sameAsShipping;
};

export const selectWholeState = (state: StateData) => state;

export const selectIsPws = (state: StateData) => state.isPws;

export const selectTotals = (state: StateData) => state.totals;

export const selectTotalsCalculated = (state: StateData) => state.totalsCalculated;

export const selectError = (state: StateData) => state.error;
export const selectHasCheckoutError = (state: StateData) => state.checkoutError;

export const selectEditingControls = (state: StateData) => state.controls.editing;

export const selectIsEditing = (state: StateData) => {
  const { autoship, shipping, cardInfo, billingAddress } = state.controls.editing;
  return shipping || cardInfo || billingAddress || autoship;
};

export const selectEditingAlert = (state: StateData) => state.controls.editingAlert;

export const selectSnackbar = (state: StateData) => state.controls.snackbar;

// cart-specific selectors
export const selectHideErrorModal = (state: StateData) => state.hideErrorModal;

export const selectGuestOrderLimitExceeded = (state: StateData) => state.guestOrderLimitExceeded;

export const selectCartItems = (state: StateData) => {
  return state.items;
};

export const selectCartInfo = (state: StateData<Cart>) => {
  return {
    billAddress: state.billAddress,
    shipping: state.shipping,
    attributes: {
      isPvq: state.attributes.isPvq,
    },
  };
};

export const selectCartAccessToken = (state: StateData<Cart>) => state.accessToken ?? false;

export const selectCartCouponCode = (state: StateData<Cart>) => state.attributes.couponCodeId ?? null;

export const selectCartCanAutoship = (state: StateData<Cart>) => state.attributes.autoShipRules.canAutoShip ?? false;

export const selectCartCanAutoshipQuarterly = (state: StateData<Cart>) =>
  state.attributes.autoShipRules.canAutoShipQuarterly ?? false;

export const selectIsAutoship = (state: StateData<Cart>) => state.shipping.data.isAutoShip ?? false;

export const selectCartIsSignupOrder = (state: StateData<Cart>) =>
  state.attributes?.enrollmentRules?.orderIsEnrollment ?? false;

export const selectCartIsPvq = (state: StateData<Cart>) => state.attributes.isPvq ?? false;

export const selectCartSuggestedKalogrisDonations = (state: StateData): {quantity: number, description: string}[] | false =>
  state.attributes.kalogrisDonations.possibleAmounts ?? false;

export const selectCartBillingAddress = (state: StateData<Cart>) => state.billAddress;

export const selectCartShipViaOptions = (state: StateData<Cart>) => state.shipViaOptions;

export const selectCartShippingAddress = (state: StateData<Cart>) => state.shipping?.data?.shipAddress;
export const selectCartShippingAddressLine2 = (state: StateData<Cart>) => state.shipping?.data?.shipAddress.addressLine2;

export const selectCartAllShippingInfo = (state: StateData<Cart>) => state.shipping;

export const selectCartShippingName = (state: StateData<Cart>) => state.shipping.data.shipAddress.nameLine;

export const selectCartPhone = (state: StateData<Cart>) => state.shipping.data.shipAddress.phone;

export const selectCartTotals = (state: StateData<Cart>) => state.orderTotals;

export const selectCartAutoshipInfo = (state: StateData<Cart>) => {
  const autoshipInfo = {
    isAutoShip: state.shipping.data.isAutoShip,
    autoShipIsMonthly: state.shipping.data.autoShipIsMonthly,
  };
  return autoshipInfo;
};
export const selectShipVia = (state: StateData<Cart>) => state.shipping.data.shipVia;
export const selectCartShipZip = (state: StateData<Cart>) => state.shipping.data.shipAddress.postalCode;
export const selectCartCanPvq = (state: StateData<Cart>) => state.attributes.pvqRules.displayPvq;
export const selectCartGeoCode = (state: StateData<Cart>) => state.shipping.data.shipAddress.geoCode;
export const selectCartHasEmergencyCheckoutWarningMessage = (state: StateData<Cart>) =>
  state.attributes.emergencyCheckoutWarningMessage;

export const selectCartPaymentType = (state: StateData) => state.cartPaymentType;

export const selectCartPaymentInfo = (state: StateData) => state.cartPaymentInfo;
export const selectDisableSubmitOrder = (state: StateData<Cart>) => state.disableSubmitOrder;

export const selectAvailablePaymentTypes = (state: StateData<Cart>) =>
  state.attributes.paymentRules.availablePaymentTypes;

export const selectBillAddress = (state: StateData<Cart>) => state.billAddress;

export const selectCartSameAsShipping = (state: StateData<Cart>) => state.controls.sameAsShipping;
export const selectOrderId = (state: StateData<Cart>) => state.id;

export const selectDisableAutoComplete = (state: StateData<Cart>) => state.disableAutoComplete;

export const selectCartDataLoaded = (state: StateData<Cart>) => state.controls.cartDataLoaded;

export const selectOrderTotals = (state: StateData<Cart>): StateData['orderTotals'] => state.orderTotals;

export const selectSelectedKalogrisDonation = (state: StateData<Cart>): number => {
  const cartLoaded = Boolean(selectCartAccessToken(state));
  if (!cartLoaded) {
    return 0;
  }
  const orderItem = state.items.find(
    (item) => item.skuNumber === 'KFCSH'
  );
  if (orderItem) {
    return orderItem.qty;
  }

  return 0;
}

export const selectHasBadCoupon = (state: StateData): boolean => state.controls.hasBadCoupon